import { useMutation } from '@tanstack/react-query';
import React from 'react';

import { create, remove } from '../../api/ventura/reportTemplates';
import { useConfig } from '../../hooks/useConfig';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSnackBar } from '../../hooks/useSnackBar';
import { useReportingHubPageContext } from '../../pages/ReportingHubPageContext';
import { ReportTemplate } from '../../validationSchemas/ReportTemplateSchema';
import ReportTemplateDefaultList from './ReportTemplateDefaultList';
import ReportTemplateList from './ReportTemplateList';

interface ReportTemplateSectionProps {
  name: string;
  userList: { id: number; name: string }[];
}

function ReportTemplateSection({ name, userList }: ReportTemplateSectionProps) {
  const { refetch } = useReportingHubPageContext();
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const [user] = useLocalStorage('user', null);
  const { id: userId } = user;

  const duplicateMutation = useMutation({
    mutationFn: (reportTemplate: ReportTemplate) =>
      create(apiHost, userId, {
        ...reportTemplate,
        templateId: null,
        userId,
        reportName: `Copy of ${reportTemplate.reportName}`,
      }),
    onError: (error: Error) => {
      showSnackBar(error.message, 'error', {
        event: 'report_template_save_error',
        user_id: userId,
      });
    },
  });

  const duplicateReportTemplateList = async (selectedReportTemplates, confirmMessage) => {
    if (await confirmMessage()) {
      const workload = [];
      selectedReportTemplates.forEach((selectedReportTemplate: ReportTemplate) => {
        workload.push(() => duplicateMutation.mutateAsync(selectedReportTemplate));
      });

      for (let i = 0; i < workload.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await workload[i]();
      }

      showSnackBar('The report templates were successfully duplicated.', 'success', {
        event: 'report_template_duplicated',
        user_id: userId,
        count: selectedReportTemplates.length,
      });
      if (refetch) {
        refetch();
      }
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (templateId: number) => remove(apiHost, userId, templateId),
    onError: (error: Error) => {
      showSnackBar(error.message, 'error', {
        event: 'report_template_delete_error',
        user_id: userId,
      });
    },
  });

  const deleteReportTemplateList = async (selectedReportTemplates, confirmMessage) => {
    // Check to make sure all the template belongs to the user
    for (let i = 0; i < selectedReportTemplates.length; i += 1) {
      const reportTemplate = selectedReportTemplates[i];
      if (reportTemplate.userId !== userId) {
        showSnackBar(`You can only delete your own report templates.`, 'error', {
          event: 'report_template_delete_error',
          user_id: userId,
        });
        return;
      }
    }

    if (await confirmMessage()) {
      const workload = [];
      selectedReportTemplates.forEach((selectedReportTemplate) => {
        workload.push(() => deleteMutation.mutateAsync(Number(selectedReportTemplate.templateId)));
      });

      for (let i = 0; i < workload.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await workload[i]();
      }

      showSnackBar('The report templates were successfully deleted.', 'success', {
        event: 'report_template_deleted',
        user_id: userId,
        count: selectedReportTemplates.length,
      });
      if (refetch) {
        refetch();
      }
    }
  };

  return (
    <>
      <ReportTemplateDefaultList userName={name} duplicateReportTemplateList={duplicateReportTemplateList} />
      <ReportTemplateList
        userList={userList || []}
        duplicateReportTemplateList={duplicateReportTemplateList}
        deleteReportTemplateList={deleteReportTemplateList}
      />
    </>
  );
}

export default ReportTemplateSection;
